import React, { useEffect, useRef, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, Upload, Loader2, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { LockedModule } from "./SchoolAnalytics";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const MAX_MB = 15;

const resolveUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u || ""}`);

export default function SchoolBrochure() {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const mod = modules.find((m) => m.key === "school_brochure");
  const owned = mod?.owned;

  const load = async () => {
    const [m, s] = await Promise.all([
      api.get(`${V1}/school-modules`),
      user?.school_id ? api.get(`${V1}/schools/${user.school_id}`) : Promise.resolve({ data: {} }),
    ]);
    setModules(m.data || []);
    setUrl(s.data?.brochure_url || "");
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.school_id]);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Yalnızca PDF dosyası kabul edilir");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Dosya en fazla ${MAX_MB} MB olmalı`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`${V1}/school/media/brochure/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUrl(data.brochure_url);
      toast.success("Broşür başarıyla yüklendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeBrochure = async () => {
    if (!window.confirm("Broşürü kaldırmak istiyor musun?")) return;
    try {
      await api.delete(`${V1}/school/media/brochure`);
      setUrl("");
      toast.success("Broşür kaldırıldı");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silinemedi");
    }
  };

  if (!mod) return <div className="text-muted-foreground">Yükleniyor…</div>;
  if (!owned) return <LockedModule mod={mod} testid="brochure-locked" />;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="school-brochure-page">
      <div>
        <div className="overline text-accent">Modül · {mod.label}</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><FileText className="h-7 w-7 text-primary" /> Okul Broşürü</h1>
        <p className="text-muted-foreground text-sm mt-1">
          PDF broşürünüzü doğrudan yükleyin — ziyaretçiler okul sayfanızdan indirebilir. Maksimum {MAX_MB} MB.
          Yükleme sırasında dosya güvenlik doğrulamasından geçirilir.
        </p>
      </div>

      <div className="p-6 rounded-xl border-2 border-dashed border-border bg-card flex flex-col items-center gap-3 text-center">
        <FileText className="h-10 w-10 text-muted-foreground" />
        <div className="font-medium">PDF Broşürünü Yükle</div>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={onFile}
          data-testid="brochure-file-input"
        />
        <Button onClick={onPick} disabled={uploading} data-testid="brochure-upload-btn">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Yükleniyor…" : "PDF Seç ve Yükle"}
        </Button>
      </div>

      {url && (
        <div className="p-4 rounded-xl border border-border bg-card flex items-center justify-between gap-3" data-testid="brochure-current">
          <div className="flex items-center gap-2 text-sm">
            <FileText className="h-5 w-5 text-primary" />
            <span className="font-medium">Yayında olan broşür</span>
          </div>
          <div className="flex items-center gap-2">
            <a href={resolveUrl(url)} target="_blank" rel="noreferrer" className="text-sm text-primary underline flex items-center gap-1" data-testid="brochure-preview-link">
              <ExternalLink className="h-3.5 w-3.5" /> Önizle
            </a>
            <Button variant="outline" size="sm" onClick={removeBrochure} data-testid="brochure-remove-btn">
              <Trash2 className="h-3.5 w-3.5" /> Kaldır
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
